"use client";

import { useState } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { TemplateCard } from "./template-card";
import { TEMPLATES, CATEGORIES, type ConnectionTemplate } from "./templates-data";

interface TemplatesGridProps {
  onSelectTemplate: (template: ConnectionTemplate) => void;
  compact?: boolean;
}

export function TemplatesGrid({ onSelectTemplate, compact = false }: TemplatesGridProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredTemplates = TEMPLATES.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className={cn("space-y-4", compact && "space-y-3")}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
          <input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 w-full rounded-lg border border-white/[0.06] bg-white/[0.02] pl-9 pr-4 font-mono text-[12px] text-white/80 placeholder:text-white/40 outline-none transition-colors focus:border-teal-500/30 focus:bg-white/[0.04]"
          />
        </div>

        <div className="flex flex-wrap gap-1.5">
          {CATEGORIES.map((category) => {
            const isActive = selectedCategory === category.id;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={cn(
                  "flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-medium transition-all duration-200",
                  isActive
                    ? "bg-teal-500/15 text-teal-300"
                    : "border border-white/[0.06] bg-white/[0.015] text-white/60 hover:bg-white/[0.04] hover:text-white/50",
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full transition-colors",
                    isActive ? "bg-teal-400" : category.dot,
                  )}
                />
                {category.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] tabular-nums text-white/40">
          {filteredTemplates.length} template{filteredTemplates.length !== 1 ? "s" : ""}
        </span>
        {selectedCategory !== "all" && (
          <>
            <span className="text-[10px] text-white/35">&middot;</span>
            <button
              onClick={() => setSelectedCategory("all")}
              className="text-[10px] text-teal-400/40 transition-colors hover:text-teal-400/70"
            >
              Clear filter
            </button>
          </>
        )}
      </div>

      <div className={cn(
        "grid gap-3",
        compact
          ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
          : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4",
      )}>
        {filteredTemplates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            onClick={() => onSelectTemplate(template)}
          />
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-white/[0.06] py-16 text-center">
          <Search className="mb-3 h-5 w-5 text-white/35" />
          <h3 className="text-[13px] font-medium text-white/40">No templates found</h3>
          <p className="mt-1.5 text-[13px] text-white/40">Try adjusting your search or filter</p>
        </div>
      )}
    </div>
  );
}

export { TEMPLATES, CATEGORIES };
export type { ConnectionTemplate };
